import React, { createContext, useState, useEffect, useContext } from 'react';
import apiClient from '../api/ApiClient';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      const currentUser = await apiClient.getCurrentUser();
      setUser(currentUser);
      setError(null);
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      // Don't set error for 401, just means not logged in
      if (err.status !== 401) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, remember = true) => {
    try {
      setError(null);
      const userData = await apiClient.login(email, password, remember);
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await apiClient.logout();
      setUser(null);
      setError(null);
    } catch (err) {
      console.error('Logout failed:', err);
      // Still clear user even if logout fails
      setUser(null);
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    setUser,
    apiClient
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
